import React, { useMemo, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  ExpandableSection,
  Header,
  Modal,
  Pagination,
  SpaceBetween,
  Table,
  TextContent,
  ButtonDropdown,
  ButtonDropdownProps,
  Link,
  Spinner,
} from '@amzn/awsui-components-react';
import { Credentials } from 'aws-sdk';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import LocalizedTimeAgo from '../../../common/LocalizedTimeAgo';
import { useApi } from '../../../../store/api.context';
import { useChallenges } from '../../../../store/challenge.context';
import { useFlashbars } from '@/src//store/flashbar.context';
import { errorFlashbar, successFlashbar } from '@/src//utils/notification.utils';

import {
  Challenge,
  ChallengeReviewableSection,
  ChallengeUtils,
  TemplateScannerResponse,
} from '../../../../types/Challenge';
import { Nullable } from '../../../../types/common';
import { AccountCredentials } from '../../../../types/LabModels';
import { getEnvVar } from '../../../../utils/env-var.utils';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { preProdLogger } from '../../../../utils/log.utils';
import { uploadFile } from '../../../../utils/s3-utils';
import { safeString } from '../../../../utils/string.utils';
import AwsAccountCLIModal from '../../../common/Challenges/AwsAccountCLIModal';
import { CopyToClipboard } from '../../../common/CopyToClipboard';
import { copyToClipboard } from '@/src/utils/copy-to-clipboard';

import { ChallengeReviewPopover } from '../../challengesCommon/ChallengeReviewPopover';
import JamCodeEditor from '../../challengesCommon/JamCodeEditor';
import { useUser } from '../../../../store/user.context';
import './Resources.scss';
import FileDragAndDrop from '@/src/components/common/FileDragAndDrop';
import { S3Object } from '@/src/types/s3-object';

interface ChallengeResourcesDetailProps {
  challenge: Challenge;
  postUploadRefreshTimeout?: number;
}

interface ChallengeResource extends S3Object {
  uploadInProgress?: boolean;
}

const DEFAULT_TIMEOUT = 5000;

/**
 * Allowlist of MIME types accepted for challenge resource uploads.
 *
 * Challenge resources are CloudFormation templates, Lambda deployment bundles, supporting
 * config/scripts and images used in challenge content.
 *
 * text/html and image/svg+xml are deliberately excluded: uploaded objects are served from the
 * bucket via a direct URL, so a script-bearing document would execute in the bucket origin
 * when opened. Note SVG is excluded even though other image formats are allowed - it is an
 * XML document that can carry <script> and inline event handlers, whereas PNG and JPEG are
 * raster formats the browser decodes rather than executes.
 *
 * Browsers report inconsistent MIME types for YAML and shell/Python scripts depending on
 * the OS, so the common variants are listed alongside the canonical ones, plus the empty
 * string for extensions the browser cannot classify at all.
 *
 * NOTE: this is a client-side control for user experience only. It is bypassable by anyone
 * holding the per-challenge STS upload credentials and must be enforced server-side as well.
 */
const CHALLENGE_RESOURCE_MIME_TYPES = [
  // Archives (Lambda bundles, asset packs)
  'application/zip',
  'application/x-zip-compressed',
  'application/gzip',
  'application/x-gzip',
  'application/x-tar',
  // Templates and structured config
  'application/json',
  'application/x-yaml',
  'application/yaml',
  'text/yaml',
  'text/x-yaml',
  // Text, config and data
  'text/plain',
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
  'text/tab-separated-values',
  'text/markdown',
  // NOTE: generic XML (application/xml, text/xml, .xml) is intentionally NOT allowed. Like SVG,
  // an XML document served inline from the bucket origin can execute script - e.g. an
  // <?xml-stylesheet?> XSLT instruction that transforms it into HTML, or an XHTML-namespaced
  // document.
  'application/toml',
  // Source files across common challenge languages. These are safe to store and serve: the
  // browser treats them as text or downloads them - it never executes a source file opened
  // from a direct URL (a .js file only runs when loaded as script by an HTML page, which we
  // exclude). So the risk is the same as the plain text already allowed, regardless of language.
  'application/x-sh',
  'text/x-sh',
  'application/x-shellscript',
  'text/x-python',
  'text/x-python-code',
  'text/x-script.python',
  'application/x-python-code',
  'text/javascript',
  'application/javascript',
  'application/x-javascript',
  // Some OSes map .ts to the MPEG transport stream type rather than a text type; allow it so
  // TypeScript source is not silently rejected. The extension layer is the real gate here.
  'video/mp2t',
  'text/x-typescript',
  'application/typescript',
  'text/x-java-source',
  'text/x-java',
  'text/x-csrc',
  'text/x-c',
  'text/x-c++src',
  'text/x-go',
  'text/x-ruby',
  'text/x-rust',
  'application/x-httpd-php',
  'text/x-php',
  // Images used in challenge content. Raster formats only - see note above re: SVG.
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  // Video for challenge walkthroughs/demos. Container formats the browser decodes as media,
  // not active documents - they cannot carry executable script the way HTML/SVG can.
  'video/mp4',
  'video/webm',
  'video/quicktime',
  // PDF for challenge instructions/diagrams. Unlike the raster formats above, a PDF is not
  // fully inert - it can carry JavaScript (/OpenAction, /JavaScript) that some in-browser
  // viewers may run, and these objects are served from a public bucket origin. Risk is lower
  // than SVG (browser PDF viewers sandbox and largely block script) but not zero; the
  // Content-Disposition: attachment follow-up on the serving side is what fully neutralizes it.
  'application/pdf',
  // Browsers frequently report no type for .yaml/.yml/.sh and similar extensions
  '',
];

/**
 * Extension allowlist for challenge resource uploads, paired with CHALLENGE_RESOURCE_MIME_TYPES.
 *
 * This second layer is required because the MIME list above tolerates the empty string for
 * extensions browsers cannot classify. Without an extension check, a file with a stripped or
 * renamed extension would report no type and pass on the MIME layer alone. A file must satisfy
 * both layers to be accepted.
 *
 * Grouped by category so the same source of truth drives both enforcement and the user-facing hint.
 * Source files are safe to serve because the browser never executes one opened from a direct URL; .html/.htm
 * and .svg are intentionally absent because those render as active documents.
 */
const CHALLENGE_RESOURCE_EXTENSION_GROUPS = {
  archives: ['zip', 'gz', 'tgz', 'tar'],
  configData: ['json', 'yaml', 'yml', 'txt', 'csv', 'tsv', 'md', 'toml', 'ini', 'properties', 'log'],
  sourceFiles: ['sh', 'py', 'js', 'ts', 'tsx', 'jsx', 'java', 'go', 'rb', 'rs', 'c', 'h', 'cpp', 'cs', 'php'],
  images: ['png', 'jpg', 'jpeg', 'gif', 'webp'],
  video: ['mp4', 'webm', 'mov'],
  documents: ['pdf'],
} as const;

const CHALLENGE_RESOURCE_EXTENSIONS = Object.values(CHALLENGE_RESOURCE_EXTENSION_GROUPS).flat();

const formatExtensions = (extensions: readonly string[]): string => extensions.map((ext) => `.${ext}`).join(', ');

const getAllowedFileTypesText = (t: TFunction): string => {
  const allowed = t(i18nKeys.challenges.subSections.assetsResources.allowedFileTypes, {
    archives: formatExtensions(CHALLENGE_RESOURCE_EXTENSION_GROUPS.archives),
    configData: formatExtensions(CHALLENGE_RESOURCE_EXTENSION_GROUPS.configData),
    sourceFiles: formatExtensions(CHALLENGE_RESOURCE_EXTENSION_GROUPS.sourceFiles),
    images: formatExtensions(CHALLENGE_RESOURCE_EXTENSION_GROUPS.images),
    video: formatExtensions(CHALLENGE_RESOURCE_EXTENSION_GROUPS.video),
    documents: formatExtensions(CHALLENGE_RESOURCE_EXTENSION_GROUPS.documents),
  });
  const exclusion = t(i18nKeys.challenges.subSections.assetsResources.allowedFileTypesExclusion);
  return `${allowed} ${exclusion}`;
};

const ChallengeResourcesDetail: React.FC<ChallengeResourcesDetailProps> = ({
  challenge,
  postUploadRefreshTimeout = DEFAULT_TIMEOUT,
}) => {
  const { t } = useTranslation();
  const { challengesApi } = useApi();
  const { challengeResources, getChallengeResources } = useChallenges();
  const { user } = useUser();
  const { addFlashbar, addInfoFlashbar } = useFlashbars();

  const resourceBucket = getEnvVar('REACT_APP_CHALLENGE_RESOURCES_BUCKET_NAME');
  const resourcesBucketUrl = `https://${resourceBucket}.s3.amazonaws.com`;

  const [credentials, setCredentials] = useState<AccountCredentials | undefined>();
  const [templateScanResults, setTemplateScanResults] = useState('');
  const [filesBeingUploaded, setFilesBeingUploaded] = useState<ChallengeResource[] | null>(null);
  const [visibleSTSCredentialsModal, setVisibleSTSCredentialsModal] = useState(false);
  const [cfnSnippetModal, setCfnSnippetModal] = useState<{ visible: boolean; snippet: string }>({
    visible: false,
    snippet: '',
  });

  const challengeResourcesEnhanced: ChallengeResource[] = useMemo(() => {
    if (!filesBeingUploaded?.length) return challengeResources;
    return [...challengeResources, ...filesBeingUploaded];
  }, [challengeResources, filesBeingUploaded]);

  useEffect(() => {
    if (ChallengeUtils.isCollaborator(challenge, user)) {
      const getSTSCreds = async () =>
        setCredentials(await challengesApi.getCredentialsToUploadChallengeResources(challenge.challengeId || ''));

      getSTSCreds().catch((err) => preProdLogger(err));
      void getChallengeResources(challenge.challengeId || '');
    }
  }, []);

  const getResourcesBucketChallengePath = (challengeId: string): string => {
    return `${resourcesBucketUrl}/${challengeId}`;
  };

  const getS3Uri = (challengeId: string): string => {
    return `s3://${resourceBucket}/${challengeId}`;
  };

  const getS3SyncCommand = () => {
    return `aws s3 sync . ${getS3Uri(challenge.challengeId || '')}`;
  };

  const getCfnSnippetForResource = (resourceKey: string): string => {
    const s3Key = `${challenge.challengeId}/${resourceKey}`;
    const bucketRef = `!Sub ${resourceBucket}-\${AWS::Region}`;
    return [
      '# S3 Bucket (regional):',
      `S3Bucket: ${bucketRef}`,
      '',
      '# S3 Key:',
      `S3Key: ${s3Key}`,
      '',
      '# Example: Lambda Function Code property',
      'Code:',
      '  S3Bucket:',
      `    ${bucketRef}`,
      '  S3Key:',
      `    ${s3Key}`,
    ].join('\n');
  };

  const deleteChallengeResource = async (key: string) => {
    await challengesApi.deleteChallengeResource(challenge.challengeId || '', key);
  };

  const validateS3Template = () => {
    challengesApi
      .validateS3Template(challenge.challengeId || '', true)
      .then((response: TemplateScannerResponse) => setTemplateScanResults(response.result))
      .catch((err) => preProdLogger(err));
  };

  const onReceiveFiles = (files: File[]) => {
    challengesApi
      .getCredentialsToUploadChallengeResources(challenge.challengeId || '')
      .then((accountCredentials) => {
        const creds: Nullable<Credentials> = accountCredentials.toAWSCredentials();
        if (creds) {
          setFilesBeingUploaded(
            files.map((f) => ({ key: f.name, size: f.size, uploadInProgress: true, lastModified: null }))
          );
          Promise.all(
            Object.keys(files)?.map((file, i) => {
              return uploadFile(creds, resourceBucket, files[i], challenge.challengeId);
            })
          )
            .then(() =>
              addFlashbar(
                successFlashbar(
                  t(i18nKeys.general.success),
                  t(i18nKeys.challenges.subSections.assetsResources.uploadSuccessMessage)
                )
              )
            )
            .catch((err) => preProdLogger(err));
        }
        setTimeout(() => {
          // refresh the table after upload the element after 5 seconds
          void getChallengeResources(challenge.challengeId || '').finally(() => setFilesBeingUploaded(null));
        }, postUploadRefreshTimeout);
      })
      .catch((err) => preProdLogger(err));
  };

  const tableDataClick = async (e: ButtonDropdownProps.ItemClickDetails) => {
    const data = e.id.split('||');
    if (data[0] && data[1]) {
      switch (data[0]) {
        case 'copy': {
          const copyData = `${getResourcesBucketChallengePath(challenge.challengeId || '')}/${data[1]}`;
          copyToClipboard(copyData);
          addFlashbar(successFlashbar(t(i18nKeys.clipboard.successCopiedToClipboard), ''));
          break;
        }
        case 'open': {
          const openUrl = `${getResourcesBucketChallengePath(challenge.challengeId || '')}/${data[1]}`;
          window.open(openUrl, '_blank', 'noopener,noreferrer');
          break;
        }
        case 'view':
          setCfnSnippetModal({ visible: true, snippet: getCfnSnippetForResource(data[1]) });
          break;
        case 'delete':
          await deleteChallengeResource(data[1]);
          addInfoFlashbar(t(i18nKeys.challenges.subSections.assetsResources.assestDelete));
          // refresh the table after deleting the element
          void getChallengeResources(challenge.challengeId || '');
          break;
        default:
          addFlashbar(errorFlashbar('Action not supported', ''));
      }
    }
  };

  return (
    <div className="challenge-resources">
      <SpaceBetween direction="vertical" size="m">
        {challengeResources === undefined ||
          (challengeResources.length === 0 && (
            <Container
              header={
                <Header
                  variant="h2"
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        data-testid="challenge-resources-refresh"
                        onClick={() => void getChallengeResources(challenge.challengeId || '')}
                      >
                        {t(i18nKeys.challenges.subSections.assetsResources.refresh)}
                      </Button>
                      <Button
                        data-testid="challenge-resources-validate-template"
                        variant="primary"
                        onClick={validateS3Template}
                      >
                        {t(i18nKeys.challenges.subSections.assetsResources.validateTemplates)}
                      </Button>
                      <ChallengeReviewPopover section={ChallengeReviewableSection.ASSETS_RESOURCES} />
                    </SpaceBetween>
                  }
                >
                  {t(i18nKeys.challenges.challengeDetails.headings.assetsResources)}
                </Header>
              }
            >
              <SpaceBetween direction="vertical" size="m">
                <Box padding={'l'} textAlign="center">
                  <p>{t(i18nKeys.challenges.subSections.assetsResources.noResourceMessage)}</p>
                  <Button
                    data-testid="challenge-resources-assets-refresh"
                    onClick={() => void getChallengeResources(challenge.challengeId || '')}
                  >
                    {t(i18nKeys.challenges.subSections.assetsResources.refresh)}
                  </Button>
                </Box>
              </SpaceBetween>
            </Container>
          ))}
        {challengeResources.length > 0 && (
          <Table
            data-testid="resource-table"
            columnDefinitions={[
              {
                id: 'file',
                header: t(i18nKeys.challenges.challengeDetails.table.titles.fileName),
                cell: (e) => e.key,
              },
              {
                id: 'size',
                header: t(i18nKeys.challenges.challengeDetails.table.titles.size),
                cell: (e) => e.size?.toLocaleString(),
                sortingField: 'size',
              },
              {
                id: 'lastModified',
                header: t(i18nKeys.challenges.challengeDetails.table.titles.lastModified),
                cell: (e) =>
                  e.lastModified ? <LocalizedTimeAgo datetime={safeString(e.lastModified.toString())} /> : '--',
              },
              {
                id: 'actions',
                header: t(i18nKeys.general.actions),
                cell: (e) => (
                  <div>
                    {!!e?.uploadInProgress ? (
                      <Spinner />
                    ) : (
                      <>
                        {' '}
                        <ButtonDropdown
                          expandToViewport
                          items={[
                            {
                              id: `copy||${e.key}`,
                              text: t(i18nKeys.challenges.subSections.assetsResources.actions.copyUrl) || '',
                            },
                            {
                              id: `open||${e.key}`,
                              text: t(i18nKeys.challenges.subSections.assetsResources.actions.viewResource) || '',
                            },
                            {
                              id: `view||${e.key}`,
                              text: t(i18nKeys.challenges.subSections.assetsResources.actions.viewSnippet) || '',
                            },
                            {
                              id: `delete||${e.key}`,
                              text: t(i18nKeys.general.delete) || '',
                            },
                          ]}
                          onItemClick={({ detail }) => void tableDataClick(detail)}
                          ariaLabel={t(i18nKeys.general.actions)}
                          variant="inline-icon"
                        />
                      </>
                    )}
                  </div>
                ),
              },
            ]}
            items={challengeResourcesEnhanced}
            loadingText={t(i18nKeys.challenges.challengeDetails.table.loadingResources)}
            empty={
              <Box textAlign="center" color="inherit">
                <b>{t(i18nKeys.challenges.subSections.assetsResources.noResourceMessage)}</b>
                <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                  {t(i18nKeys.challenges.subSections.assetsResources.noResourceMessage)}
                </Box>
                <Button>{t(i18nKeys.challenges.subSections.assetsResources.refresh)}</Button>
              </Box>
            }
            header={
              <Header
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      data-testid="challenge-resources-assets-resource"
                      onClick={() => void getChallengeResources(challenge.challengeId || '')}
                    >
                      {t(i18nKeys.challenges.subSections.assetsResources.refresh)}
                    </Button>
                    <Button
                      data-testid="challenge-resources-validate-template-asset"
                      variant="primary"
                      onClick={validateS3Template}
                    >
                      {t(i18nKeys.challenges.subSections.assetsResources.validateTemplates)}
                    </Button>
                    <ChallengeReviewPopover section={ChallengeReviewableSection.ASSETS_RESOURCES} />
                  </SpaceBetween>
                }
              >
                {t(i18nKeys.challenges.challengeDetails.table.headers.resources)}
              </Header>
            }
            pagination={
              <Pagination
                currentPageIndex={1}
                pagesCount={1}
                ariaLabels={{
                  nextPageLabel: t(i18nKeys.tables.pagination.nextPage),
                  previousPageLabel: t(i18nKeys.tables.pagination.prevPage),
                  pageLabel: (pageNumber) => t(i18nKeys.tables.pagination.label, { count: pageNumber }),
                }}
              />
            }
          />
        )}
        {
          <Container
            header={
              <Header
                variant="h2"
                actions={<ChallengeReviewPopover section={ChallengeReviewableSection.ASSETS_RESOURCES} />}
              >
                {t(i18nKeys.challenges.subSections.assetsResources.uploadFilesHeader)}
              </Header>
            }
          >
            <SpaceBetween size="s">
              <FileDragAndDrop
                multiple
                onUpdate={onReceiveFiles}
                validMimes={CHALLENGE_RESOURCE_MIME_TYPES}
                validExtensions={CHALLENGE_RESOURCE_EXTENSIONS}
                constraintText={getAllowedFileTypesText(t)}
              />
              <div>
                <Header variant="h2">{t(i18nKeys.challenges.challengeDetails.headings.cliInstructions)}</Header>
                <TextContent>
                  <ol>
                    <li>{t(i18nKeys.challenges.subSections.assetsResources.cliInstructions.step1)}</li>
                    <li>
                      {t(i18nKeys.challenges.subSections.assetsResources.cliInstructions.step2Copy)}
                      <Link
                        data-testid="challenge-resources-show-credential-modal"
                        onFollow={() => setVisibleSTSCredentialsModal(true)}
                      >
                        {t(i18nKeys.challenges.subSections.assetsResources.cliInstructions.step2Credentials)}
                      </Link>
                      {t(i18nKeys.challenges.subSections.assetsResources.cliInstructions.step2Terminal)}
                    </li>
                    <li>
                      {t(i18nKeys.challenges.subSections.assetsResources.cliInstructions.step3)}
                      <SpaceBetween direction="horizontal" size="s">
                        <span style={{ color: '#962249' }}>{getS3SyncCommand()} </span>
                        <CopyToClipboard
                          variant="inline-link"
                          value={getS3SyncCommand()}
                          buttonLabel={t(i18nKeys.clipboard.buttonLabel)}
                          successMessage={t(i18nKeys.clipboard.successCopiedToClipboard)}
                        />
                      </SpaceBetween>
                    </li>
                    <li>
                      {t(i18nKeys.challenges.subSections.assetsResources.cliInstructions.step4)} <br />
                      <strong> {getResourcesBucketChallengePath(challenge.challengeId || '')}</strong>
                    </li>
                  </ol>
                </TextContent>
              </div>
            </SpaceBetween>
          </Container>
        }
        {templateScanResults.length > 0 && (
          <ExpandableSection
            variant="container"
            header={<Header>{t(i18nKeys.challenges.challengeDetails.headings.templateSecurityFindings)}</Header>}
          >
            <JamCodeEditor
              language="yaml"
              value={templateScanResults}
              setValue={() => {
                preProdLogger('none');
              }}
            />
          </ExpandableSection>
        )}
        {credentials && (
          <AwsAccountCLIModal
            visible={visibleSTSCredentialsModal}
            onCancel={() => setVisibleSTSCredentialsModal(false)}
            onConfirm={() => setVisibleSTSCredentialsModal(false)}
            credentials={credentials}
          />
        )}
        <Modal
          visible={cfnSnippetModal.visible}
          onDismiss={() => setCfnSnippetModal({ visible: false, snippet: '' })}
          header={t(i18nKeys.challenges.subSections.assetsResources.actions.viewSnippet)}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <CopyToClipboard
                  variant="normal"
                  value={cfnSnippetModal.snippet}
                  buttonLabel={t(i18nKeys.clipboard.buttonLabel)}
                  successMessage={t(i18nKeys.clipboard.successCopiedToClipboard)}
                />
                <Button variant="primary" onClick={() => setCfnSnippetModal({ visible: false, snippet: '' })}>
                  {t(i18nKeys.general.close)}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="s">
            <TextContent>
              <p>{t(i18nKeys.challenges.subSections.assetsResources.actions.viewSnippetDescription)}</p>
            </TextContent>
            <JamCodeEditor
              language="yaml"
              value={cfnSnippetModal.snippet}
              setValue={() => {
                preProdLogger('none');
              }}
            />
          </SpaceBetween>
        </Modal>
      </SpaceBetween>
    </div>
  );
};

export default ChallengeResourcesDetail;
